import { Icon } from "@iconify/react";
import { Button, Col, Form, Input, Row, Space, Typography } from "antd";
import { Formik } from "formik";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { OnBoarding } from "~/Components";
import { useNotification } from "~/Hooks/useNotification";
import { useQuery } from "~/Hooks/useQuery";
import { post } from "~/Services";
import { ReactComponent as PassLock } from "../../../Assets/SVG/lock.svg";

const { Title, Text, Link } = Typography;
export const ConfirmNewPassword = () => {
  const navigate = useNavigate();
  const { openToast } = useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const query = useQuery();
  const email = query.get("emailAddress");
  const validationSchema = Yup.object({
    newPassword: Yup.string()
      .required("New password is required")
      .min(14, "Password must be at least 14 characters long")
      .matches(/[a-z]/, "Password must contain at least one lowercase letter")
      .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
      .matches(/[0-9]/, "Password must contain at least one number")
      .matches(/[^A-Za-z0-9]/, "Password must contain at least one special character"),
    confirmNewPassword: Yup.string()
      .oneOf([Yup.ref("newPassword")], "Passwords must match")
      .required("Confirm your password"),
  });

  const handleSetPassword = (values: any) => {
    setIsLoading(true);
    post("auth/reset-password", values, { "X-Auth-Type": "manual" })
      .then((res: any) => {
        const message = res?.message;
        openToast({ content: message, type: "success" });
        navigate("/auth/login");
      })
      .catch((err: any) => {
        const message = err?.message;
        if (message) {
          openToast({ content: message, type: "error" });
        }
        console.error("Error:", err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <OnBoarding>
      <Row style={{ marginBottom: 25 }}>
        <Col span={24}>
          <Title level={2}>Reset Password</Title>
        </Col>
      </Row>
      <Formik
        initialValues={{
          newPassword: "",
          confirmNewPassword: "",
          emailAddress: email,
        }}
        validationSchema={validationSchema}
        onSubmit={handleSetPassword}
      >
        {({ handleSubmit, handleChange, handleBlur, values, errors, touched, isValid, dirty }) => (
          <Form
            layout="vertical"
            onFinish={handleSubmit}
          >
            <Space
              direction="vertical"
              size="large"
              style={{ width: "100%" }}
            >
              <Form.Item
                label="New Password"
                validateStatus={touched.newPassword && errors.newPassword ? "error" : ""}
                help={touched.newPassword && errors.newPassword}
                style={{ marginBottom: 24 }}
              >
                <Input.Password
                  name="newPassword"
                  prefix={<PassLock />}
                  iconRender={(visible) =>
                    visible ? (
                      <Icon
                        icon="mdi:eye-outline"
                        width={18}
                        height={18}
                      />
                    ) : (
                      <Icon
                        icon="mdi:eye-off-outline"
                        width={18}
                        height={18}
                      />
                    )
                  }
                  size="large"
                  placeholder="Create your new password"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.newPassword}
                />
              </Form.Item>

              <Form.Item
                label="Confirm Password"
                validateStatus={touched.confirmNewPassword && errors.confirmNewPassword ? "error" : ""}
                help={touched.confirmNewPassword && errors.confirmNewPassword}
                style={{ marginBottom: 24 }}
              >
                <Input.Password
                  name="confirmNewPassword"
                  prefix={<PassLock />}
                  iconRender={(visible) =>
                    visible ? (
                      <Icon
                        icon="mdi:eye-outline"
                        width={18}
                        height={18}
                      />
                    ) : (
                      <Icon
                        icon="mdi:eye-off-outline"
                        width={18}
                        height={18}
                      />
                    )
                  }
                  size="large"
                  placeholder="Confirm your new password"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.confirmNewPassword}
                />
              </Form.Item>

              <Form.Item>
                <Button
                  disabled={!(dirty && isValid)}
                  loading={isLoading}
                  type="primary"
                  size="large"
                  block
                  htmlType="submit"
                >
                  Reset Password
                </Button>
              </Form.Item>
            </Space>
          </Form>
        )}
      </Formik>
    </OnBoarding>
  );
};
